import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { useNavigation } from '../../../../navigation/NavigationProvider';
import { Button } from '../../../../core/design-system/components/Button';
import { Input } from '../../../../core/design-system/components/Input';
import { Text } from '../../../../core/design-system/components/Text';
import GoogleAuthButton from '../../components/buttons/GoogleAuthButton/index';
import { Logo } from '../../../../core/design-system/components/Logo';
import './styles.css';

const SignUp = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { signUp } = useAuth();
  const { goToDashboard } = useNavigation();
  const { t } = useTranslation('auth');

  // Fonction pour valider l'email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction pour valider le mot de passe
  const validatePassword = (password) => {
    // Au moins 8 caractères, une lettre majuscule, une lettre minuscule et un chiffre
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  // Validation à chaque changement d'input
  useEffect(() => {
    const newErrors = {};
    
    if (touched.email && email && !validateEmail(email)) {
      newErrors.email = t('errors.invalid_email_format');
    }
    
    if (touched.password && password && !validatePassword(password)) {
      newErrors.password = t('errors.password_requirements');
    }
    
    if (touched.confirmPassword && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = t('errors.password_mismatch');
    }
    
    setErrors(prev => ({...prev, ...newErrors}));
  }, [email, password, confirmPassword, touched, t]);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Marquer tous les champs comme touchés
    setTouched({
      name: true,
      email: true,
      password: true,
      confirmPassword: true
    });

    if (!name) newErrors.name = t('errors.required_field');
    if (!email) newErrors.email = t('errors.required_field');
    else if (!validateEmail(email)) newErrors.email = t('errors.invalid_email_format');
    
    if (!password) newErrors.password = t('errors.required_field');
    else if (!validatePassword(password)) newErrors.password = t('errors.password_requirements');
    
    if (!confirmPassword) newErrors.confirmPassword = t('errors.required_field');
    else if (password !== confirmPassword) newErrors.confirmPassword = t('errors.password_mismatch');

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      await signUp(email, password, name);
      // Rediriger vers le dashboard
      goToDashboard();
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: t('errors.email_already_exists') });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: t('errors.invalid_email') });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ password: t('errors.weak_password') });
      } else {
        setErrors({ general: t('errors.default') });
      }
    }
  };

  return (
    <div className="signup-content">
      <div className="signup-header">
        <Logo size={56} />
        <Text variant="h1" className="signup-title">
          {t('signup.title')}
        </Text>
      </div>

      <div className="social-buttons">
        <GoogleAuthButton />
      </div>

      <div className="divider">{t('signup.divider', 'ou')}</div>

      <form onSubmit={handleSubmit}>
        <div className="input-container">
          <Input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors({ ...errors, name: undefined });
            }}
            onBlur={() => handleBlur('name')}
            placeholder={t('signup.name')}
            error={errors.name}
          />
          {touched.name && !errors.name && name && (
            <div className="field-hint">
              <Text variant="caption" color="success">
                {t('signup.valid_name')}
              </Text>
            </div>
          )}
        </div>

        <div className="input-container">
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors({ ...errors, email: undefined });
            }}
            onBlur={() => handleBlur('email')}
            placeholder={t('signup.email')}
            error={errors.email}
          />
          {touched.email && !errors.email && email && validateEmail(email) && (
            <div className="field-hint">
              <Text variant="caption" color="success">
                {t('signup.valid_email')}
              </Text>
            </div>
          )}
        </div>

        <div className="input-container">
          <Input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors({ ...errors, password: undefined });
            }}
            onBlur={() => handleBlur('password')}
            placeholder={t('signup.password')}
            error={errors.password}
          />
          {!errors.password && touched.password && (
            <div className="password-requirements">
              <Text variant="caption" color={validatePassword(password) ? "success" : "secondary"}>
                {t('signup.password_requirements')}
              </Text>
            </div>
          )}
        </div>

        <div className="input-container">
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
            }}
            onBlur={() => handleBlur('confirmPassword')}
            placeholder={t('signup.confirm_password')}
            error={errors.confirmPassword}
          />
          {touched.confirmPassword && !errors.confirmPassword && confirmPassword && password === confirmPassword && (
            <div className="field-hint">
              <Text variant="caption" color="success">
                {t('signup.passwords_match')}
              </Text>
            </div>
          )}
        </div>

        <div className="terms-container">
          <Text variant="caption" className="terms-text">
            {t('signup.terms_prefix')} 
            <a href="/terms" className="terms-link">{t('signup.terms_link')}</a> 
            {t('signup.and')} 
            <a href="/privacy" className="terms-link">{t('signup.privacy_link')}</a>
          </Text>
        </div>

        {errors.general && (
          <div className="general-error">
            <Text variant="body2" color="error">
              {errors.general}
            </Text>
          </div>
        )}

        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          fullWidth
          disabled={Object.keys(errors).some(key => errors[key])}
        >
          {t('signup.submit')}
        </Button>
      </form>
    </div>
  );
};

export default SignUp; 